<template>
  <div class="jobs-erp-page">
    <nav class="page-breadcrumb mb-1">
      <feather-icon icon="HomeIcon" size="14" />
      <span class="ml-25">Configurações</span>
      <feather-icon icon="ChevronRightIcon" size="14" class="mx-25" />
      <router-link :to="{ name: 'integracao-erp' }">Integração ERP</router-link>
      <feather-icon icon="ChevronRightIcon" size="14" class="mx-25" />
      <span class="font-weight-bold">Jobs</span>
    </nav>

    <b-card class="mb-1">
      <b-row class="align-items-end">
        <b-col md="4" sm="12">
          <label class="small text-muted">Pesquisar</label>
          <b-form-input v-model="filters.q" size="sm" placeholder="Nome ou descrição" @keydown.enter="buscar" />
        </b-col>
        <b-col md="3" sm="6">
          <label class="small text-muted">Integração</label>
          <b-form-select v-model="filters.integracaoid" size="sm" @change="buscar">
            <b-form-select-option :value="null">Todas</b-form-select-option>
            <b-form-select-option v-for="i in integracoesDisponiveis" :key="i.integracaoid" :value="i.integracaoid">
              {{ i.nome }}
            </b-form-select-option>
          </b-form-select>
        </b-col>
        <b-col md="2" sm="6">
          <label class="small text-muted">Status</label>
          <b-form-select v-model="filters.status" size="sm" @change="buscar">
            <b-form-select-option :value="null">Todos</b-form-select-option>
            <b-form-select-option value="ATIVO">Ativo</b-form-select-option>
            <b-form-select-option value="PAUSADO">Pausado</b-form-select-option>
            <b-form-select-option value="INATIVO">Inativo</b-form-select-option>
          </b-form-select>
        </b-col>
        <b-col md="3" sm="12" class="d-flex align-items-end" style="gap:6px">
          <b-button size="sm" variant="primary" @click="buscar" :disabled="carregando">
            <feather-icon icon="SearchIcon" size="13" />
          </b-button>
          <b-button size="sm" variant="outline-secondary" @click="limparFiltros">
            <feather-icon icon="XIcon" size="13" />
          </b-button>
          <b-button size="sm" variant="relief-success" class="ml-auto" @click="abrirNovo">
            <feather-icon icon="PlusIcon" size="13" class="mr-25" />Novo Job
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <b-card>
      <div v-if="carregando" class="text-center py-4"><b-spinner /></div>
      <div v-else-if="!lista.length" class="text-center py-4 text-muted">
        <feather-icon icon="InboxIcon" size="36" class="mb-1" /><br>
        Nenhum job cadastrado.
      </div>
      <b-table
        v-else
        :items="lista"
        :fields="fields"
        hover
        responsive
        small
      >
        <template #cell(nome)="{ item }">
          <strong>{{ item.nome }}</strong>
          <div v-if="item.descricao" class="text-muted small">{{ item.descricao }}</div>
        </template>
        <template #cell(integracao_nome)="{ item }">
          <span :title="item.integracao_url">{{ item.integracao_nome || '—' }}</span>
        </template>
        <template #cell(cron_expression)="{ item }">
          <code v-if="item.cron_expression">{{ item.cron_expression }}</code>
          <span v-else class="text-muted">—</span>
        </template>
        <template #cell(status)="{ item }">
          <b-badge :variant="statusVariant(item)">
            {{ statusLabel(item) }}
          </b-badge>
        </template>
        <template #cell(ultimo_status)="{ item }">
          <span v-if="!item.ultima_execucao" class="text-muted">Nunca</span>
          <template v-else>
            <b-badge :variant="item.ultimo_status === 'SUCESSO' ? 'light-success' : 'light-danger'">
              {{ item.ultimo_status }} <span v-if="item.ultimo_status_http">· {{ item.ultimo_status_http }}</span>
            </b-badge>
            <div class="text-muted small">{{ formatDate(item.ultima_execucao) }}</div>
          </template>
        </template>
        <template #cell(acoes)="{ item }">
          <b-button size="sm" variant="flat-success" class="mr-25" title="Executar agora"
                    :disabled="executando" @click="executar(item)">
            <feather-icon icon="PlayIcon" size="13" />
          </b-button>
          <b-button size="sm" variant="flat-warning" class="mr-25" title="Mapeamento"
                    :to="{ name: 'jobs-erp-mapeamento', params: { id: item.jobid } }">
            <feather-icon icon="GitMergeIcon" size="13" />
          </b-button>
          <b-button size="sm" variant="flat-info" class="mr-25" title="Histórico"
                    :to="{ name: 'jobs-erp-execucoes', params: { id: item.jobid } }">
            <feather-icon icon="ListIcon" size="13" />
          </b-button>
          <b-button size="sm" variant="flat-primary" class="mr-25" title="Editar" @click="editar(item)">
            <feather-icon icon="EditIcon" size="13" />
          </b-button>
          <b-button size="sm" variant="flat-danger" title="Excluir" @click="confirmarExcluir(item)">
            <feather-icon icon="Trash2Icon" size="13" />
          </b-button>
        </template>
      </b-table>

      <div v-if="meta.totalPages > 1" class="d-flex justify-content-end mt-1">
        <b-pagination
          v-model="currentPage"
          :total-rows="meta.total"
          :per-page="meta.pageSize"
          size="sm"
          @change="onPageChange"
        />
      </div>
    </b-card>

    <JobModal ref="modalForm" :integracoes="integracoesDisponiveis" @saved="onSaved" />
    <ResultadoExecucaoModal ref="modalResultado" />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import IntegracaoErpClient from '@/api/Integracao/integracaoErp.client';
import JobModal from './modal/JobModal.vue';
import ResultadoExecucaoModal from './modal/ResultadoExecucaoModal.vue';

const integracaoClient = new IntegracaoErpClient();

export default {
  name: 'JobsErp',
  components: { JobModal, ResultadoExecucaoModal },
  data() {
    return {
      filters: { q: '', status: null, integracaoid: null },
      currentPage: 1,
      integracoesDisponiveis: [],
      fields: [
        { key: 'nome',            label: 'Nome' },
        { key: 'integracao_nome', label: 'Integração' },
        { key: 'cron_expression', label: 'Cron' },
        { key: 'status',          label: 'Status' },
        { key: 'ultimo_status',   label: 'Última Execução' },
        { key: 'acoes',           label: '', class: 'text-right', thStyle: { width: '240px' } },
      ],
    };
  },
  computed: {
    ...mapState('JobErp', ['lista', 'meta', 'carregando', 'executando']),
  },
  async created() {
    await Promise.all([this.carregarIntegracoes(), this.buscar()]);
  },
  methods: {
    ...mapActions('JobErp', ['listar', 'excluir', 'executar']),
    async carregarIntegracoes() {
      try {
        const { data } = await integracaoClient.listar({ pageSize: 200 });
        this.integracoesDisponiveis = data && data.data ? data.data : [];
      } catch (e) {
        this.integracoesDisponiveis = [];
      }
    },
    async buscar() {
      await this.listar({
        q: this.filters.q || undefined,
        status: this.filters.status || undefined,
        integracaoid: this.filters.integracaoid || undefined,
        page: this.currentPage,
        pageSize: 20,
      });
    },
    async limparFiltros() {
      this.filters = { q: '', status: null, integracaoid: null };
      this.currentPage = 1;
      await this.buscar();
    },
    onPageChange(page) {
      this.currentPage = page;
      this.buscar();
    },
    abrirNovo() { this.$refs.modalForm.open(null); },
    editar(item) { this.$refs.modalForm.open(item); },
    async executar(item) {
      try {
        const resultado = await this.$store.dispatch('JobErp/executar', item.jobid);
        this.$refs.modalResultado.open(item, resultado);
        await this.buscar();
      } catch (e) {
        this.$bvToast.toast(`Falha ao executar: ${e.message || e}`, { title: 'Erro', variant: 'danger', solid: true });
      }
    },
    async confirmarExcluir(item) {
      const ok = await this.$bvModal.msgBoxConfirm(
        `Excluir o job "${item.nome}"? O histórico de execuções também será removido.`,
        { title: 'Confirmar exclusão', okTitle: 'Excluir', okVariant: 'danger', cancelTitle: 'Cancelar', centered: true },
      );
      if (!ok) return;
      await this.excluir(item.jobid);
      this.$bvToast.toast('Job excluído.', { title: 'OK', variant: 'success', solid: true });
      await this.buscar();
    },
    onSaved() { this.buscar(); },
    statusVariant(item) {
      if (!item.ativo) return 'light-secondary';
      if (item.status === 'PAUSADO') return 'light-warning';
      if (item.status === 'INATIVO') return 'light-secondary';
      return 'light-success';
    },
    statusLabel(item) {
      if (!item.ativo) return 'Desativado';
      return item.status || 'ATIVO';
    },
    formatDate(v) {
      if (!v) return '';
      try { return new Date(v).toLocaleString('pt-BR'); } catch (e) { return v; }
    },
  },
};
</script>

<style scoped>
.page-breadcrumb { display: flex; align-items: center; font-size: 0.9rem; color: #6e6b7b; }
.page-breadcrumb a { color: #6e6b7b; }
.page-breadcrumb a:hover { color: #7367f0; }
</style>
